import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'

interface MobileNavProps {
  currentPath?: string
}

// Primary navigation items for mobile bottom nav
const primaryNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/transactions', label: 'Tapahtumat', icon: '💰' },
  { to: '/invoices', label: 'Laskut', icon: '📄' },
  { to: '/members', label: 'Jäsenet', icon: '👥' },
]

// More menu items (shown in modal/sheet)
const moreNavItems = [
  { to: '/journal', label: 'Päiväkirja', icon: '📒' },
  { to: '/general-ledger', label: 'Pääkirja', icon: '📚' },
  { to: '/reports', label: 'Raportit', icon: '📈' },
  { to: '/vat-returns', label: 'ALV', icon: '🧾' },
  { to: '/reconciliation', label: 'Täsmäytys', icon: '🔄' },
  { to: '/files', label: 'Tiedostot', icon: '📎' },
  { to: '/settings/integrations', label: 'Asetukset', icon: '⚙️' },
]

export default function MobileNav({ currentPath: _currentPath }: MobileNavProps) {
  const location = useLocation()
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  // Check if current path is in more menu
  const isMoreActive = moreNavItems.some(item => 
    location.pathname === item.to || location.pathname.startsWith(item.to + '/')
  )

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 pb-safe z-40">
        <div className="flex items-center justify-around h-16">
          {primaryNavItems.map(({ to, label, icon }) => {
            const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
            return (
              <Link
                key={to}
                to={to}
                className={`flex flex-col items-center justify-center min-w-[64px] min-h-[44px] px-2 py-1 rounded-lg transition ${
                  isActive
                    ? 'text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="text-xl mb-0.5" aria-hidden="true">{icon}</span>
                <span className="text-[10px] font-medium leading-tight">{label}</span>
              </Link>
            )
          })}
          
          {/* More menu button */}
          <button
            onClick={() => setShowMoreMenu(true)}
            className={`flex flex-col items-center justify-center min-w-[64px] min-h-[44px] px-2 py-1 rounded-lg transition ${
              isMoreActive
                ? 'text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="text-xl mb-0.5" aria-hidden="true">☰</span>
            <span className="text-[10px] font-medium leading-tight">Lisää</span>
          </button>
        </div>
      </nav>

      {/* More Menu Modal */}
      {showMoreMenu && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
            onClick={() => setShowMoreMenu(false)}
          />
          <div className="fixed bottom-20 left-4 right-4 bg-white rounded-xl shadow-2xl z-50 lg:hidden max-h-[70vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Valikko</h3>
                <button
                  onClick={() => setShowMoreMenu(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Sulje"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {moreNavItems.map(({ to, label, icon }) => {
                  const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setShowMoreMenu(false)}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl transition min-h-[80px] ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-600'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-2xl mb-1" aria-hidden="true">{icon}</span>
                      <span className="text-xs font-medium text-center leading-tight">{label}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Safe area spacer for bottom nav */}
      <div className="lg:hidden h-16" />
    </>
  )
}
